import PeopleIcon from '@mui/icons-material/People';
import SearchIcon from '@mui/icons-material/Search';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import getBrandGroups from '../../../../../api/oc_product/brandGroup/get/getBrandGroup';
import postBrandGroup from '../../../../../api/oc_product/brandGroup/post/postBrandGroup';
import { BrandGroupingContext } from '../BrandGroupingContext';

function BrandGroupingHeader({
  brandGroups, setBrandGroups, totalGroupsCount = 432, activeTab, setActiveTab,
}) {
  const siteId = useSelector((state) => state.user.loginResponse?.selectedSite?.SiteId);
  const { brands, setFetchData, setReFetchLogs } = useContext(BrandGroupingContext);

  const [filteredBrands, setFilteredBrands] = React.useState([]);
  const [searchValue, setSearchValue] = React.useState('');
  const [searchType, setSearchType] = React.useState('brand');
  const [addGroup, setAddGroup] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [newGroupBrand, setNewGroupBrand] = React.useState(null);
  const [newGroupBrands, setNewGroupBrands] = React.useState([]);

  React.useEffect(() => {
    if (searchValue !== '') {
      getBrandGroups(siteId, searchType, searchValue)
        .then((res) => {
          setBrandGroups(res.data);
        })
        .catch((err) => console.error(err));
    } else {
      getBrandGroups(siteId)
        .then((res) => {
          setBrandGroups(res.data);
        })
        .catch((err) => console.error(err));
    }
  }, [searchValue, searchType, siteId]);

  React.useEffect(() => {
    const filteredBrandsArr = brands?.filter((brand) => !brandGroups?.some((group) => group.BrandGroupName === brand.Name));
    setFilteredBrands(filteredBrandsArr);
  }, [brandGroups, brands]);

  React.useEffect(() => {
    if (searchValue.length > 0) {
      setSearchValue('');
    }
  }, [siteId]);

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setNewGroupBrand(null);
    setNewGroupBrands([]);
  };

  const handleCreateBrandGroup = () => {
    if (newGroupBrand) {
      const selectedBrandIds = newGroupBrands.map((brand) => brand.BrandId);
      const body = {
        groupName: newGroupBrand.Name,
        brandId: selectedBrandIds,
      };
      postBrandGroup(siteId, body)
        .then(() => {
          setFetchData(true);
          setReFetchLogs(true);
          toast.success('Brand Group Created');
          handlePopoverClose();
        })
        .catch((err) => console.error(err));
    }
  };

  const open = Boolean(anchorEl);

  const customRenderOption = (props, option) => (
    <li {...props} key={option.brandId}>
      <div>{option.Name}</div>
    </li>
  );

  return (
    <Box className="w-full">
      <Box className="flex items-start justify-between mb-6">
        <Box className="flex flex-col">
          <Typography
            variant="h3"
            sx={{
              color: '#384451',
              marginBottom: 1,
            }}
          >
            Brand Grouping
          </Typography>

          <Box className="flex items-center mt-4">
            <TextField
              placeholder="Search by Brand or Group"
              variant="outlined"
              value={searchValue}
              size="small"
              onChange={(event) => setSearchValue(event.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon color="action" sx={{ mr: 1, color: '#495057' }} />
                ),
                sx: {
                  borderRadius: '25px',
                  backgroundColor: '#e6f2f5',
                  paddingLeft: '10px',
                  height: '48px',
                  '& fieldset': { border: 'none' },
                },
              }}
              sx={{
                width: '280px',
                marginRight: '12px',
              }}
            />
            <Select
              value={searchType}
              onChange={(event) => setSearchType(event.target.value)}
              displayEmpty
              renderValue={(selected) => (
                <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 'medium' }}>
                  {selected === 'group' ? 'Group' : 'Brand'}
                </Typography>
              )}
              sx={{
                height: '48px',
                backgroundColor: '#34495e',
                borderRadius: '25px',
                padding: '0 8px',
                minWidth: '150px',
                color: '#ffffff',
                border: searchType === 'brand' ? '1px solid #2196F3' : '1px solid #34495e',
                '& .MuiSelect-select': {
                  paddingRight: '32px',
                  paddingLeft: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#ffffff',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiSvgIcon-root': {
                  color: '#ffffff',
                },
                '&:hover': {
                  borderColor: '#2196F3',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    backgroundColor: 'white',
                    color: '#495057',
                    '& .MuiMenuItem-root': {
                      padding: '8px 16px',
                      color: '#495057',
                      '&:hover': {
                        backgroundColor: '#f0f0f0',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#E0E7E9',
                        '&:hover': {
                          backgroundColor: '#E0E7E9',
                        },
                      },
                    },
                  },
                },
              }}
              inputProps={{
                sx: {
                  color: '#ffffff',
                },
              }}
            >
              <MenuItem value="group">
                <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#495057' }}>Group</Typography>
              </MenuItem>
              <MenuItem value="brand">
                <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#495057' }}>Brand</Typography>
              </MenuItem>
            </Select>
          </Box>
        </Box>

        <Box className="flex flex-col items-end">
          <Box
            sx={{
              width: '200px',
              height: '60px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              border: '2px solid #1aa0b3',
              borderRadius: '8px',
              mb: 2,
              backgroundColor: 'white',
              boxShadow: '0 0 10px rgba(26, 160, 179, 0.2)',
              padding: '4px 8px',
            }}
          >
            <Typography variant="caption" sx={{ color: 'rgb(90, 158, 168)', fontWeight: 700 }}>Total Groups</Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#343a40' }}>{totalGroupsCount}</Typography>
          </Box>

          <Button
            onClick={(event) => {
              setAddGroup(!addGroup);
              handlePopoverOpen(event);
            }}
            variant="contained"
            sx={{
              backgroundColor: '#34495e',
              color: 'white',
              '&:hover': {
                backgroundColor: '#495057',
              },
              py: 1.5,
              px: 2,
              borderRadius: '25px',
              textTransform: 'uppercase',
              height: '48px',
            }}
          >
            <PeopleIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
            CREATE NEW GROUP
          </Button>

          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            PaperProps={{ style: { width: '400px' } }}
          >
            <Box className="flex flex-col p-8">
              <Box className="mb-3">
                <Box className="flex flex-row items-start mb-3">
                  <Box className="flex items-center justify-center w-4 h-4 p-3 mr-3 text-white rounded-full" sx={{ backgroundColor: '#1aa0b3' }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>1</Typography>
                  </Box>
                  <Box className="flex flex-col flex-grow">
                    <Typography variant="h6" className="mb-3">Select group brand</Typography>
                    <Typography variant="body2" className="mb-3">
                      The group brand can be used as the name of the group and can only be selected for one group
                    </Typography>
                    <Autocomplete
                      options={filteredBrands || []}
                      getOptionLabel={(brand) => brand?.Name ?? ''}
                      size="small"
                      onChange={(e, value) => setNewGroupBrand(value)}
                      renderInput={(params) => (<TextField {...params} label="Group Brand" />)}
                      renderOption={customRenderOption}
                    />
                  </Box>
                </Box>
              </Box>

              <Box className="mb-8">
                <Box className="flex flex-row items-start mb-3">
                  <Box className="flex items-center justify-center w-4 h-4 p-3 mr-3 text-white rounded-full" sx={{ backgroundColor: '#1aa0b3' }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>2</Typography>
                  </Box>
                  <Box className="flex flex-col w-full">
                    <Typography variant="h6" className="mb-3">Select individual brands</Typography>
                    <Autocomplete
                      multiple
                      id="brands"
                      size="small"
                      value={newGroupBrands}
                      onChange={(e, value) => setNewGroupBrands(value)}
                      options={brands || []}
                      getOptionLabel={(brand) => (brand ? brand.Name : '')}
                      limitTags={4}
                      renderInput={(params) => <TextField {...params} label="Brand" />}
                      renderOption={customRenderOption}
                    />
                  </Box>
                </Box>
              </Box>

              <Box className="flex justify-end">
                <Button
                  variant="contained"
                  onClick={handleCreateBrandGroup}
                  disabled={!newGroupBrand || newGroupBrands.length === 0}
                  sx={{ mr: 1, backgroundColor: '#1aa0b3' }}
                >
                  Save
                </Button>
                <Button variant="text" sx={{ color: '#495057' }} onClick={handlePopoverClose}>
                  Cancel
                </Button>
              </Box>
            </Box>
          </Popover>
        </Box>
      </Box>
    </Box>
  );
}

export default BrandGroupingHeader;
